import { useEffect, useState, type Dispatch, type Key, type ReactNode, type SetStateAction } from "react";
import { Modal, Table, Button, TableProps } from "antd";
import { InputSearch } from "@shared/ui";

export interface SelectModalProps<T> {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (selected: T[]) => void;
  data: T[];
  columns: TableProps<T>["columns"];
  rowKey: keyof T | ((record: T) => string);
  title?: string;
  renderExpandedRow?: (record: T) => ReactNode;
  filters?: ReactNode;
  getRowSearchText?: (row: T) => string;
  initialSelectedKeys?: Key[];
  page: number;
  limit: number;
  setPage: Dispatch<SetStateAction<number>>;
  setLimit: Dispatch<SetStateAction<number>>;
}

export function SelectModal<T extends object>({
  visible,
  onCancel,
  onConfirm,
  data,
  columns,
  rowKey,
  title = "Выберите элементы",
  renderExpandedRow,
  filters,
  getRowSearchText = (row) => JSON.stringify(row),
  initialSelectedKeys,
  page,
  limit,
  setPage,
  setLimit,
}: SelectModalProps<T>) {
  const [search, setSearch] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>(
    initialSelectedKeys ?? [],
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

  useEffect(() => {
    setSelectedRowKeys(initialSelectedKeys ?? []);
  }, [initialSelectedKeys]);

  const filteredData = data.filter((item) =>
    getRowSearchText(item).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width={1024}
      title={title}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Отменить
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={() => {
            const selected = data.filter((item) => {
              const keyValue: Key =
                typeof rowKey === "function"
                  ? rowKey(item)
                  : (item[rowKey] as Key);

              return selectedRowKeys.includes(keyValue);
            });
            onConfirm(selected);
          }}
        >
          Подтвердить выбор
        </Button>,
      ]}
    >
      {filters ?? (
        <InputSearch
          placeholder="Поиск по наименованию"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<T>
        columns={columns}
        dataSource={filteredData}
        rowKey={rowKey}
        pagination={{
          total: filteredData.length,
          current: page,
          pageSize: limit,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "50", "100"],
          onChange: (newPage, newSize) => {
            setPage(newPage);
            setLimit(newSize);
          },
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} из ${total} записей`,
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        expandable={
          renderExpandedRow && {
            expandedRowRender: renderExpandedRow,
            expandIcon: () => null,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              const key: Key =
                typeof rowKey === "function"
                  ? rowKey(record)
                  : (record[rowKey] as Key);
              setExpandedRowKeys(expanded ? [key] : []);
            },

            expandedRowClassName: () => "expand-row-animate",
          }
        }
      />
    </Modal>
  );
}

